import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, writesText } from '../skillUsage';

export const scrambleDecodeRevealSkill: CodeoSkill = {
    name: SKILL_NAMES.scrambleDecodeReveal,
    kind: 'codeo',
    description:
        'Resolve a word out of cycling glyphs, locking character by character from the left — the ' +
        'decode reveal, for a name, a codename, a category, or anything that should feel computed ' +
        'rather than typed.',
    checkLooksFor: 'a run of scramble characters, written onto the element from an onUpdate',
    documentUsesSkill: check(
        (script) =>
            writesText(script) &&
            /onUpdate/.test(script) &&
            /['"][A-Z0-9#%&$@]{12,}['"]/.test(script),
    ),
    instructions: `# Scramble Decode Reveal

A line starts as nonsense glyphs churning in place, then locks into the real string one character at a time from the left. It reads as something being *resolved* — a lookup landing, a name being identified — rather than something being written.

The whole effect is one tween on a scalar and an \`onUpdate\` that rebuilds the string. That is what makes it seek-safe: at any progress, the text is a pure function of the tween value.

## Markup

Use a monospace or tabular face and reserve the final width, or the line will visibly grow as characters lock and everything after it will jitter.

\`\`\`html
<div id="decode" style="position: absolute; left: 200px; top: 470px; font-family: var(--l5-font-0), monospace; font-size: 128px; font-weight: 800; letter-spacing: 0.04em; color: var(--l5-color-0, #101820); white-space: pre;">&nbsp;</div>
\`\`\`

## Timeline code

The glyph shown for an unlocked slot must depend only on the tween value and the slot index — never on a clock or a random draw, or every rendered frame would disagree with the last.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const TARGET = 'AUTONOMOUS';
const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&$@';
const CHURN = 9; // glyph changes per character-lock; higher reads busier
const decodeEl = document.querySelector('#decode');
const progress = { locked: 0 };

tl.to(progress, {
    locked: TARGET.length,
    duration: 1.5,
    ease: 'power1.inOut',
    onUpdate: () => {
        const settled = Math.floor(progress.locked);
        const tick = Math.floor(progress.locked * CHURN);
        let out = '';
        for (let index = 0; index < TARGET.length; index += 1) {
            if (index < settled || TARGET[index] === ' ') {
                out += TARGET[index];
            } else {
                out += GLYPHS[(index * 7 + tick) % GLYPHS.length];
            }
        }
        decodeEl.textContent = out;
    },
}, 0.2);

window.__timelines = { main: tl };
\`\`\`

\`index * 7\` decorrelates neighbouring slots so they don't churn in lockstep, which would read as a marquee instead of noise.

## Adapt

- **Lock order**: \`ease: 'power1.inOut'\` locks slowly, accelerates, then eases into the last character. \`'none'\` locks at a constant rate, which feels mechanical — good for a serial number, wrong for a word.
- **Churn rate**: \`CHURN\` around 8–12 reads as computation. Below 4 you can see individual glyphs and it reads as broken; above 20 it is a grey blur.
- **Glyph set**: restrict \`GLYPHS\` to the register you want — digits only for an identifier, katakana for a cyber look, the target's own alphabet for something quieter.
- **Lock from the middle**: compare \`index\` against a settled window centered on the string instead of a left edge.
- **Accent on lock**: layer a short color flash per character by wrapping each slot in its own span and tweening it — only worth it on a short string.
- **Pair with an entrance**: the line can fade or rise in while the scramble is already running; the churn covers the entrance.

## Pitfalls

- Never derive a glyph from \`Math.random()\` or a clock. Frames render out of order and in fresh contexts, so a random glyph re-rolls every frame and the text strobes.
- Reserve the width. \`white-space: pre\` plus a monospace face keeps the line still; a proportional face makes every lock nudge the layout.
- Don't scramble a sentence. Past about 12 characters the effect stops being legible as a resolve and becomes noise — it is for one word or a short label.
- Keep the whole decode under about 1.6s. It is an entrance, not the scene.
- \`textContent\` is rewritten every frame here, so anything else inside \`#decode\` is destroyed — keep the element dedicated to the decoding line.
- Text that scrambles is unreadable while it does so. If the narration says the word, the lock should land on it, not before it.`,
};
